import React, { useEffect, useState } from 'react';
import { useRecoilState, useRecoilValue } from 'recoil';
import { Routes, Route, useNavigate } from 'react-router-dom';
import {
  userAuthState,
  currentUserState,
  userDuelState,
  websocketState,
  userValidationStatus,
  userAvatarSelector,
} from './recoil/userState';
import { apiService } from './services/apiService';
import websocketService from './services/websocketService';
import DuelLive from './pages/DuelLive';
import DuelChart from './pages/DuelChart';
import { getLevelFromXP } from './utils/mapping';
import './App.css';

function Home() {
  const [authState] = useRecoilState(userAuthState);
  const [currentUser] = useRecoilState(currentUserState);
  const validationStatus = useRecoilValue(userValidationStatus);
  const avatar = useRecoilValue(userAvatarSelector);
  const [openDuels, setOpenDuels] = useState([]);
  const [loadingDuels, setLoadingDuels] = useState(false);
  const [yourLiveDuels, setYourLiveDuels] = useState([]);
  const [yourLastDuel, setYourLastDuel] = useState(null);
  const [loadingYourLiveDuels, setLoadingYourLiveDuels] = useState(false);
  const navigate = useNavigate();

  // Fetch open duels
  useEffect(() => {
    const fetchOpenDuels = async () => {
      try {
        setLoadingDuels(true);
        const response = await apiService.getOpenDuels();
        setOpenDuels(response.duels || []);
        console.log('Fetched open duels:', response.count);
      } catch (error) {
        console.error('Failed to fetch open duels:', error);
        setOpenDuels([]);
      } finally {
        setLoadingDuels(false);
      }
    };

    fetchOpenDuels();

    // Refresh open duels every 30 seconds
    const interval = setInterval(fetchOpenDuels, 30000);

    return () => clearInterval(interval);
  }, []);

  // Fetch user's live duels
  useEffect(() => {
    const fetchYourLiveDuels = async () => {
      if (!authState.isAuthenticated || !authState.telegramUser?.id) return;

      try {
        setLoadingYourLiveDuels(true);
       
        // Filter duels where the user is either challenger or opponent
        let userDuels = openDuels.filter(duel =>
          duel.challenger_user_id === authState.telegramUser.id ||
          duel.opponent_user_id === authState.telegramUser.id
        );
        setYourLiveDuels(userDuels);

        if (userDuels.length === 0) {
          const response = await apiService.getUserLastDuel(authState.telegramUser.id);
          if (!response?.message) {
            setYourLastDuel(response);
          }
        }

      } catch (error) {
        console.error('Failed to fetch user live duels:', error);
        setYourLiveDuels([]);
      } finally {
        setLoadingYourLiveDuels(false);
      }
    };

    fetchYourLiveDuels();

    // Refresh user live duels every 30 seconds
    const interval = setInterval(fetchYourLiveDuels, 30000);

    return () => clearInterval(interval);
  }, [authState.isAuthenticated, authState.telegramUser?.id, openDuels]);

  if (!currentUser) return null;

  return (
    <div className="App">
      <div className="app-container">
        <header className="app-header">
          <h1>⚔️ Lumos Duel Arena ⚔️</h1>
          <p>Duel your frens on Perps. Powered by Lighter.</p>
        </header>

        <main className="app-main">
          {process.env.NODE_ENV === 'development' && (
            <div className="dev-indicator">
              🚀 Development Mode - Using Mock Data
            </div>
          )}

          <section className="user-section">
            {validationStatus.isValidating ? (
              <div className="loading">Validating user...</div>
            ) : validationStatus.isAuthenticated ? (
              <div className="user-info">
                <div className="user-avatar">
                  {avatar ? (
                    <img src={avatar} alt="User avatar" />
                  ) : (
                    <div className="default-avatar">👤</div>
                  )}
                </div>
                <div className="user-details">
                  <h3>{currentUser?.username?.toUpperCase()}</h3>
                  <p>Level: {getLevelFromXP(currentUser?.xp_amount)}</p>
                  <p>{currentUser?.duel_wins}W - {currentUser?.duel_losses}L</p>
                </div>
              </div>
            ) : (
              <div className="error">
                Authentication failed: {validationStatus.error}
              </div>
            )}
          </section>

          {/* Your live duels Section */}
          {yourLiveDuels.length > 0 && (
            <section className="duel-section">
              <h2>Your Live Duel</h2>
              {loadingYourLiveDuels ? (
                <div className="loading">Loading your live duel...</div>
              ) : yourLiveDuels.length > 0 ? (
                <div className="your-live-duels-list">
                  {yourLiveDuels.map((duel) => {
                    return (
                      <div key={duel._id} className="open-duel-item">
                        <div className="duel-info">
                          <div className="duel-opponent">
                            <p>VS @{duel.opponent_username}</p>
                            <p>Time Remaining: {Math.max(0, Math.floor((new Date(duel.created_at).getTime() + duel.duration_minutes * 60 * 1000 - Date.now()) / (1000 * 60)))} minutes</p>
                          </div>
                        </div>
                        <div>
                          <button
                            className="btn view-duel-btn"
                            onClick={() => navigate(`/duel/live/${duel._id}`)}
                          >
                            View Duel
                          </button>
                        </div>
                      </div>
                    );
                  })}
                </div>
              ) : (
                <div className="no-duels">
                  <p>You don't have any live duels right now.</p>
                  <p>Join an open duel to start trading!</p>
                </div>
              )}
            </section>
          )}

          {/* Your Last Duel Section */}
          {yourLiveDuels.length === 0 && yourLastDuel && (
            <section className="duel-section">
              <h2>Your Last Duel</h2>
              <div className="open-duel-item">
                <div className="duel-info">
                  <div className="duel-opponent">
                    <p>VS @{yourLastDuel.opponent_username === currentUser.username ? yourLastDuel.challenger_username : yourLastDuel.opponent_username}</p>
                    <p>{yourLastDuel.winner_user_id === currentUser.user_id ? 'WON' : 'LOST'}</p>
                  </div>
                </div>
                <div>
                  <button
                    className="btn view-duel-btn"
                    onClick={() => navigate(`/duel/live/${yourLastDuel._id}`)}
                  >
                    View Duel
                  </button>
                </div>
              </div>
            </section>
          )}

          {/* Open Duels Section */}
          {openDuels.length > 0 && (
            <section className="duel-section">
              <h2>All Live Duels ({openDuels.length})</h2>
              {loadingDuels ? (
                <div className="loading">Loading duels...</div>
              ) : (
                <div className="open-duels-list">
                  {openDuels.map((duel) => (
                    <div key={duel._id} className="open-duel-item">
                      <div className="duel-info">
                        <div className="duel-opponent">
                          <p>@{duel.challenger_username} VS @{duel.opponent_username}</p>
                          <p>Time Remaining: {Math.max(0, Math.floor((new Date(duel.created_at).getTime() + duel.duration_minutes * 60 * 1000 - Date.now()) / (1000 * 60)))} minutes</p>
                        </div>
                      </div>
                      <div>
                        <button
                          className="btn view-duel-btn"
                          onClick={() => navigate(`/duel/live/${duel._id}`)}
                        >
                          View Duel
                        </button>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </section>
          )}
        </main>
      </div>
    </div>
  );
}

function App() {
  const [, setAuthState] = useRecoilState(userAuthState);
  const [, setCurrentUser] = useRecoilState(currentUserState);
  const [, setDuelState] = useRecoilState(userDuelState);
  const [, setWsState] = useRecoilState(websocketState);
  const [, setLastWsMessage] = useState(null);
  const navigate = useNavigate();

  // Initialize Telegram WebApp and validate user
  useEffect(() => {
    const initializeApp = async () => {
      console.log('Initializing app authentication flow...');
      let isDevelopmentMode = process.env.NODE_ENV === 'development';
      
      const webApp = window.Telegram.WebApp;
      let telegramUser = webApp?.initDataUnsafe?.user;
      const startParam = webApp?.initDataUnsafe?.start_param;

      // Handle startapp parameter for mini app deep linking
      if (startParam && startParam.startsWith('live_duel_')) {
        const duelId = startParam.replace('live_duel_', '');

        // Don't redirect if already on the chart page
        const currentPath = window.location.pathname;
        const isOnChartPage = currentPath === '/chart' || currentPath.startsWith('/chart/');

        if (isOnChartPage) {
          console.log('Already on chart page, skipping duel redirect for:', duelId);
        } else {
          navigate(`/duel/live/${duelId}`);
          return; // Don't continue with normal initialization
        }
      }

      if (telegramUser) {
        // Running inside Telegram
        console.log('Running inside Telegram with user');
      } else if (isDevelopmentMode) {
        // Development mode - use dummy user data
        console.log('Development mode: Using dummy user data');
        telegramUser = {
          id: 981419972,
          first_name: 'John',
          last_name: 'Doe',
          username: 'johndoe_dev',
          photo_url: null,
          language_code: 'en'
        };
      } else {
        console.warn('Production mode detected without Telegram WebApp. This may indicate an issue.');
        return;
      }

      if (telegramUser) {
        setAuthState(prev => ({
          ...prev,
          telegramUser,
          isValidating: true,
        }));

        try {
          if (isDevelopmentMode) {
            console.log('Development mode: Mocking user validation');
            await new Promise(resolve => setTimeout(resolve, 500));
          } else {
            await apiService.validateUser({
              telegramId: telegramUser.id,
              username: telegramUser.username,
              firstName: telegramUser.first_name,
              lastName: telegramUser.last_name,
              avatar: telegramUser.photo_url,
            });
          }

          const userDetails = await apiService.getUser(telegramUser.id);

          setAuthState(prev => ({
            ...prev,
            isValidating: false,
            isAuthenticated: true,
            validationError: null,
          }));

          setCurrentUser(userDetails);

          if (userDetails.currentDuelId) {
            try {
              let duelDetails;
              if (isDevelopmentMode) {
                duelDetails = {
                  id: 'duel_123',
                  status: 'active',
                  stake: 50.00,
                  startTime: Date.now() - 300000,
                  endTime: Date.now() + 2700000,
                  userId: telegramUser.id,
                  opponentId: 987654321,
                  opponent: { displayName: 'Jane Smith', username: 'janesmith_dev' },
                  userPosition: 'long',
                  opponentPosition: 'short'
                };
                await new Promise(resolve => setTimeout(resolve, 200));
              } else {
                duelDetails = await apiService.getDuel(userDetails.currentDuelId);
              }
              setDuelState(prev => ({ ...prev, currentDuel: duelDetails }));
            } catch (duelError) {
              console.error('Error fetching duel details:', duelError);
            }
          }

        } catch (error) {
          console.error('User validation failed:', error);
          if (isDevelopmentMode) {
            setAuthState(prev => ({ ...prev, isValidating: false, isAuthenticated: true, validationError: null, telegramUser }));
            setCurrentUser({ id: telegramUser.id, displayName: `${telegramUser.first_name} ${telegramUser.last_name}`, balance: 1000.50, pnl: 125.75, currentDuelId: 'duel_123', avatar: telegramUser.photo_url });
          } else {
            setAuthState(prev => ({ ...prev, isValidating: false, validationError: error.message || 'Validation failed' }));
          }
        }
      }
    };

    initializeApp();
  }, [setAuthState, setCurrentUser, setDuelState, navigate]);

  // Initialize WebSocket connection
  useEffect(() => {
    const initializeWebSocket = () => {
      websocketService.on('connected', () => {
        setWsState(prev => ({ ...prev, isConnected: true, reconnectAttempts: 0 }));
      });
      websocketService.on('disconnected', () => { setWsState(prev => ({ ...prev, isConnected: false })); });
      websocketService.on('message', (data) => { setLastWsMessage(data); setWsState(prev => ({ ...prev, lastMessage: data })); });
      websocketService.on('error', (error) => { console.error('WebSocket error:', error); });

      websocketService.connect();

      setTimeout(() => {
        if (websocketService.getStatus().isConnected) {
          setWsState(prev => ({ ...prev }));
        }
      }, 60000);
    };

    initializeWebSocket();
    return () => { websocketService.disconnect(); };
  }, [setWsState]);

  // Disable Telegram WebApp closing confirmation
  useEffect(() => {
    if (window.Telegram?.WebApp) {
      try {
        const webApp = window.Telegram.WebApp;
        const ver = webApp.version || '0.0';
        const [majStr] = String(ver).split('.');
        const major = parseInt(majStr || '0', 10);
        if (major >= 7 && typeof webApp.disableClosingConfirmation === 'function') {
          webApp.disableClosingConfirmation();
        }
      } catch (_) {}
    }
  }, []);


  return (
    <Routes>
      <Route path="/" element={<Home />} />
      <Route path="/duel/live/:duelId" element={<DuelLive />} />
      <Route path="/chart" element={<DuelChart />} />
      <Route path="/chart/:marketId" element={<DuelChart />} />
    </Routes>
  );
}

export default App;
